/**
 * Messages dédiés aux refus du modèle d'organisation (spec § 5, M4 de la revue finale) et du
 * registre du studio (lot O3, mode `registre`).
 *
 * Le serveur répond en français quelle que soit la langue de l'interface : afficher
 * `error.message` tel quel laissait un texte français dans l'interface anglaise. Ces codes
 * ont donc chacun une clé i18n, écrite en littéral (le gate `keys.test.js` ne voit pas une clé
 * calculée). Aucun n'est une déconnexion : ce sont des 403/409/503, jamais des 401 (`lib/api.js`).
 *
 * Un code peut porter des compléments (`details.organisation.nom`, `details.used/limit`) : la
 * fonction `variables` les passe à `t()` et choisit, au besoin, une clé de repli quand le
 * complément manque — jamais un `{{nom}}` ou un « undefined » à l'écran.
 */

/**
 * @typedef {{ key: string, fallbackKey?: string, variables?: (details: any) => Record<string, unknown>|null }} Entree
 * `variables` rend `null` quand le complément manque : on utilise alors `fallbackKey`.
 */

/** @type {Record<string, Entree>} */
const ENTRIES = {
  ALREADY_IN_ORGANIZATION: { key: 'orgErrors.alreadyInOrganization' },
  PERSONAL_SPACE: { key: 'orgErrors.personalSpace' },
  PERSONAL_SPACE_READ_ONLY: { key: 'orgErrors.personalSpaceReadOnly' },
  ALREADY_PROJECT_MEMBER: { key: 'orgErrors.alreadyProjectMember' },
  MEMBER_SUSPENDED: { key: 'orgErrors.memberSuspended' },
  JOIN_REQUEST_PENDING: { key: 'orgErrors.joinRequestPending' },
  EMAIL_NOT_VERIFIED: { key: 'orgErrors.emailNotVerified' },

  // Registre du studio (O3).
  SEAT_REQUIRED: { key: 'orgErrors.seatRequired' },
  MSAL_REQUIS: { key: 'orgErrors.msalRequis' },
  REGISTRE_INDISPONIBLE: { key: 'orgErrors.registreIndisponible' },
  REGISTRE_OCCUPE: { key: 'orgErrors.registreOccupe' },
  SEAT_LIMIT_REACHED: {
    key: 'orgErrors.seatLimitReached',
    fallbackKey: 'orgErrors.seatLimitReachedSansDecompte',
    variables: (details) =>
      Number.isFinite(details?.used) && Number.isFinite(details?.limit)
        ? { used: details.used, limit: details.limit }
        : null,
  },
  SIEGE_SUSPENDU: { key: 'orgErrors.siegeSuspendu' },
  SIEGE_SUSPENDU_GRACE: { key: 'orgErrors.siegeSuspenduGrace' },
  ROLE_INSUFFISANT: { key: 'orgErrors.roleInsuffisant' },
  REACTIVATION_PROPRE_REFUSEE: { key: 'orgErrors.reactivationPropreRefusee' },
  FACTURATION_SANS_SIEGE: { key: 'orgErrors.facturationSansSiege' },
  CANAL_REFUSE: { key: 'orgErrors.canalRefuse' },
  ETAT_MODIFIE: { key: 'orgErrors.etatModifie' },
  SIEGE_SOI_MEME: { key: 'orgErrors.siegeSoiMeme' },
  DERNIER_PROPRIETAIRE: { key: 'orgErrors.dernierProprietaire' },
  ORG4_ADHESION_EXISTANTE: {
    key: 'orgErrors.org4AdhesionExistante',
    fallbackKey: 'orgErrors.org4AdhesionExistanteSansNom',
    variables: (details) =>
      typeof details?.organisation?.nom === 'string' && details.organisation.nom
        ? { name: details.organisation.nom }
        : null,
  },
  PLAFOND_CREATION: { key: 'orgErrors.plafondCreation' },
  ARBITRAGE_REQUIS: { key: 'orgErrors.arbitrageRequis' },
  ADHESION_REQUISE: { key: 'orgErrors.adhesionRequise' },
};

/**
 * Message traduit pour un refus du modèle d'organisation ou du registre, `null` pour toute autre
 * erreur — l'appelant garde alors son message générique.
 * @param {unknown} error
 * @param {(key: string, options?: object) => string} t
 * @returns {string|null}
 */
export function messageModeleOrganisation(error, t) {
  const entree = ENTRIES[error?.details?.code];
  if (!entree) return null;
  if (!entree.variables) return t(entree.key);
  const variables = entree.variables(error.details);
  return variables ? t(entree.key, variables) : t(entree.fallbackKey);
}
